import React, { useEffect, useState, useMemo, useCallback } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../auth/AuthContext";
import { usePermissions } from "../hooks/usePermissions";
import { getPendingUsers } from "../api/adminUsers";
import { useSidebarStore } from "../store/sidebarStore";
import "../styles/sidebar.css";

import {
  LayoutDashboard,
  ScanSearch,
  Activity,
  UserRoundSearch,
  GlobeLock,
  BellRing,
  ShieldCheck,
  ClipboardCheck,
  ShieldUser,
  KeyRound,
  Network,
  ScrollText,
  MessagesSquare,
  PanelLeftClose,
  PanelLeftOpen,
  Lock,
  X,
  ShieldAlert,
} from "lucide-react";

import logo from "../assets/logo2.png";

export interface NavItemDef {
  id: string;
  to: string;
  label: string;
  icon: React.ComponentType<{ size?: number; className?: string; style?: React.CSSProperties }>;
  domId?: string;
  adminOnly?: boolean;
  end?: boolean;
}

export interface NavGroupDef {
  id: string;
  label: string;
  items: NavItemDef[];
  adminOnly?: boolean;
}

export default function Sidebar() {
  const { user } = useAuth();
  const { canAccess, showLockedTabs, role } = usePermissions();
  const { isCollapsed, isMobileOpen, toggleCollapsed, setMobileOpen } = useSidebarStore();
  const location = useLocation();

  const storedRole = localStorage.getItem("role");
  const currentRole = (user?.role ?? storedRole ?? "client").toLowerCase();
  const isAdmin = currentRole === "admin" || currentRole === "super_admin";
  const isAnalyst = currentRole === "analyst";

  const [pendingCount, setPendingCount] = useState(0);
  const [lockedModal, setLockedModal] = useState<{ open: boolean; label: string }>({
    open: false,
    label: "",
  });

  // Fetch pending user count for Admin badge
  useEffect(() => {
    if (!isAdmin) return;

    let isMounted = true;
    const fetchCount = async () => {
      try {
        const res = await getPendingUsers();
        if (isMounted) setPendingCount(res.total);
      } catch (err) {
        console.error("Failed to fetch pending user count in Sidebar", err);
      }
    };

    fetchCount();
    const interval = setInterval(fetchCount, 60000);
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [isAdmin]);

  // Keyboard shortcut Ctrl+B or Cmd+B to toggle sidebar collapsed state
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) {
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "b") {
        e.preventDefault();
        toggleCollapsed();
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [toggleCollapsed]);

  // Lock body scroll on mobile drawer open, and handle Escape key
  useEffect(() => {
    if (isMobileOpen) {
      document.body.style.overflow = "hidden";
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Escape") {
          setMobileOpen(false);
        }
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => {
        document.body.style.overflow = "";
        window.removeEventListener("keydown", handleKeyDown);
      };
    } else {
      document.body.style.overflow = "";
    }
  }, [isMobileOpen, setMobileOpen]);

  // Close mobile drawer on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname, setMobileOpen]);

  // Navigation Groups: Intelligence, Monitoring & Response, Administration
  const navigationGroups: NavGroupDef[] = useMemo(() => {
    return [
      {
        id: "intelligence",
        label: "Intelligence",
        items: [
          { id: "dashboard", to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, end: true },
          { id: "intelligence_feed", to: "/leaks", label: "Intelligence Feed", icon: ScanSearch, end: false },
          { id: "timeline", to: "/timeline", label: "Timeline", icon: Activity, end: false },
        ],
      },
      {
        id: "monitoring_response",
        label: "Monitoring & Response",
        items: [
          { id: "customer_check", to: "/customer-check", label: "Customer Check", icon: UserRoundSearch, end: false },
          { id: "domain_monitor", to: "/domain-monitor", label: "Domain Monitor", icon: GlobeLock, end: false },
          { id: "alert_config", to: "/alerts", label: "Alert Config", icon: BellRing, end: false },
          { id: "takedowns", to: "/takedowns", label: "Takedowns", icon: ShieldCheck, end: false },
          {
            id: "review",
            to: "/contribute",
            label: isAdmin ? "Review" : "Contribute",
            icon: ClipboardCheck,
            end: false,
          },
        ],
      },
      {
        id: "administration",
        label: "Administration",
        adminOnly: true,
        items: [
          { id: "admin_dashboard", to: "/admin", label: "Admin Dashboard", icon: ShieldUser, end: true },
          { id: "access_management", to: "/access", label: "Access", icon: KeyRound, domId: "nav-access-management", end: false },
          { id: "engagement_board", to: "/engagement-board", label: "Engagement Board", icon: Network, end: false },
          { id: "audit_logs", to: "/admin/audit", label: "Audit Logs", icon: ScrollText, domId: "nav-audit-logs", end: false },
          {
            id: "forum_monitoring",
            to: "/admin/forum-monitoring",
            label: "Forum Monitoring",
            icon: MessagesSquare,
            domId: "nav-forum-monitoring",
            adminOnly: true,
            end: false,
          },
        ],
      },
    ];
  }, [isAdmin]);

  const handleLockedClick = (label: string) => {
    setLockedModal({ open: true, label });
    toast.warning(`Access to ${label} is restricted for your role.`);
  };

  const showAdminSection =
    isAdmin ||
    isAnalyst ||
    navigationGroups
      .find((g) => g.id === "administration")
      ?.items.some((item) => canAccess(item.id)) ||
    (showLockedTabs && isAdmin);

  // Render a Single Nav Link Item
  const renderItem = useCallback(
    (item: NavItemDef, isCollapsedMode: boolean) => {
      const hasAccess = canAccess(item.id);

      if (hasAccess) {
        return (
          <div key={item.id} className="tx-nav-item-wrap">
            <NavLink
              to={item.to}
              end={item.end}
              className={({ isActive }) => `tx-nav-item${isActive ? " active" : ""}`}
              id={item.domId}
              title={isCollapsedMode ? item.label : undefined}
            >
              {({ isActive }) => (
                <>
                  {isActive && <div className="tx-active-indicator" />}
                  <div className="tx-nav-icon">
                    <item.icon size={isCollapsedMode ? 22 : 20} />
                  </div>
                  <span className="tx-nav-label">{item.label}</span>
                  {item.id === "admin_dashboard" && pendingCount > 0 && (
                    <span className="tx-nav-badge" title={`${pendingCount} pending users`}>
                      {pendingCount}
                    </span>
                  )}
                </>
              )}
            </NavLink>

            {isCollapsedMode && (
              <div className="tx-collapsed-tooltip" role="tooltip">
                <span>{item.label}</span>
                {item.id === "admin_dashboard" && pendingCount > 0 && (
                  <span style={{ color: "#f87171", fontWeight: 700 }}>({pendingCount})</span>
                )}
              </div>
            )}
          </div>
        );
      }

      // If user lacks access and admin elected to hide locked tabs:
      if (!showLockedTabs) {
        return null;
      }

      // User lacks access but showLockedTabs is enabled: show locked tab
      return (
        <div key={item.id} className="tx-nav-item-wrap">
          <div
            className="tx-nav-item locked"
            onClick={() => handleLockedClick(item.label)}
            id={item.domId}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleLockedClick(item.label);
              }
            }}
          >
            <div className="tx-nav-icon">
              <item.icon size={isCollapsedMode ? 22 : 20} />
            </div>
            <span className="tx-nav-label">{item.label}</span>
            <div className="tx-lock-icon" title="Restricted Feature">
              <Lock size={13} />
            </div>
          </div>

          {isCollapsedMode && (
            <div className="tx-collapsed-tooltip" role="tooltip">
              <span>{item.label}</span>
              <span style={{ color: "#f59e0b", fontSize: "0.7rem", display: "inline-flex", alignItems: "center", gap: 2 }}>
                <Lock size={10} /> Locked
              </span>
            </div>
          )}
        </div>
      );
    },
    [canAccess, showLockedTabs, pendingCount]
  );

  // Render Inner Sidebar Content
  const renderSidebarContent = (isCollapsedMode: boolean, isMobileView: boolean) => {
    return (
      <>
        {/* Brand Header */}
        <div className="tx-sidebar-header">
          {!isCollapsedMode ? (
            <>
              <NavLink
                to="/dashboard"
                className="tx-brand-wrap"
                title="Threatactix Dashboard"
                aria-label="Threatactix Dashboard"
              >
                <img
                  src={logo}
                  alt="Threatactix"
                  className="tx-brand-logo-expanded"
                />
              </NavLink>

              {!isMobileView && (
                <button
                  className="tx-sidebar-toggle-btn"
                  onClick={toggleCollapsed}
                  title="Collapse sidebar (Ctrl+B)"
                  aria-label="Collapse sidebar"
                >
                  <PanelLeftClose size={19} />
                </button>
              )}
            </>
          ) : (
            <div className="tx-collapsed-header-controls">
              <button
                type="button"
                className="tx-sidebar-toggle-btn collapsed"
                onClick={toggleCollapsed}
                title="Expand sidebar (Ctrl+B)"
                aria-label="Expand sidebar"
              >
                <PanelLeftOpen size={20} />
              </button>
            </div>
          )}

          {isMobileView && (
            <button
              className="tx-mobile-drawer-close"
              onClick={() => setMobileOpen(false)}
              aria-label="Close navigation"
            >
              <X size={20} />
            </button>
          )}
        </div>

        {/* Navigation Items Scroll Area */}
        <nav className="tx-sidebar-nav" aria-label="Main Navigation">
          {navigationGroups.map((group) => {
            if (group.adminOnly && !showAdminSection) return null;

            return (
              <div key={group.id} className="tx-nav-group">
                <div className="tx-nav-group-header">{group.label}</div>
                <div className="tx-nav-group-divider" />
                {group.items.map((item) => renderItem(item, isCollapsedMode))}
              </div>
            );
          })}
        </nav>

        {/* Sidebar Footer (Expand button in Collapsed Mode) */}
        {isCollapsedMode && !isMobileView && (
          <div className="tx-sidebar-footer">
            <button
              className="tx-footer-expand-btn"
              onClick={toggleCollapsed}
              title="Expand sidebar (Ctrl+B)"
              aria-label="Expand sidebar"
            >
              <PanelLeftOpen size={20} />
            </button>
          </div>
        )}
      </>
    );
  };

  return (
    <>
      {/* ── Desktop Sidebar ── */}
      <aside className={`tx-sidebar desktop-only ${isCollapsed ? "collapsed" : ""}`}>
        {renderSidebarContent(isCollapsed, false)}
      </aside>

      {/* ── Mobile Off-Canvas Drawer ── */}
      <div
        className={`tx-mobile-drawer-backdrop ${isMobileOpen ? "open" : ""}`}
        onClick={() => setMobileOpen(false)}
        role="dialog"
        aria-modal="true"
        aria-label="Mobile Navigation"
      >
        <div
          className="tx-mobile-drawer"
          onClick={(e) => e.stopPropagation()}
        >
          {renderSidebarContent(false, true)}
        </div>
      </div>

      {/* ── Feature Restricted Modal ── */}
      {lockedModal.open && (
        <div
          className="drawer-backdrop"
          style={{
            justifyContent: "center",
            alignItems: "center",
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.72)",
            backdropFilter: "blur(4px)",
            WebkitBackdropFilter: "blur(4px)",
            zIndex: 9999,
            display: "flex",
          }}
          onClick={() => setLockedModal({ open: false, label: "" })}
        >
          <div
            className="matrix-card"
            style={{
              width: 440,
              maxWidth: "92vw",
              padding: 28,
              background: "var(--color-surface, #141720)",
              border: "1px solid var(--color-border, #242938)",
              borderRadius: "16px",
              boxShadow: "0 25px 50px rgba(0, 0, 0, 0.6)",
              textAlign: "center",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                width: 52,
                height: 52,
                borderRadius: "50%",
                background: "rgba(245, 158, 11, 0.12)",
                border: "1px solid rgba(245, 158, 11, 0.3)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px",
                color: "#f59e0b",
              }}
            >
              <ShieldAlert size={26} />
            </div>

            <h3
              style={{
                margin: "0 0 8px 0",
                fontSize: "1.2rem",
                fontWeight: 700,
                color: "var(--color-text, #ffffff)",
              }}
            >
              Feature Access Restricted
            </h3>

            <p
              style={{
                fontSize: "0.86rem",
                color: "var(--color-text-muted, #94a3b8)",
                lineHeight: 1.55,
                margin: "0 0 24px 0",
              }}
            >
              Access to <strong style={{ color: "var(--color-text, #ffffff)" }}>{lockedModal.label}</strong> is
              currently locked for your role (<strong style={{ color: "#f59e0b", textTransform: "capitalize" }}>{role}</strong>).
              Your system administrator has configured this tab with restricted permissions.
            </p>

            <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
              <button
                className="btn-primary"
                onClick={() => setLockedModal({ open: false, label: "" })}
                style={{
                  minWidth: "120px",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "6px",
                }}
              >
                Understood
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
